import { html, el, trusted } from './html.js';
import { state } from './state.js';
import { SHARE, ADD_SQUARE, CLOSE } from './icons.js';
import { installTarget, dismissedRecently, dismiss } from './lib/install.js';

let kept = null; // Chrome's install event, kept until it is used
let open = null; // the dialog on screen
let dismissed = false; // this page load: storage may be blocked, and the popup must not return on every screen

const store = () => { try { return localStorage; } catch { return null; } };

// Chrome fires the event early and only once, so this runs before the app boots.
export function startInstallPrompt() {
  addEventListener('beforeinstallprompt', e => {
    e.preventDefault(); // Chrome's own mini-bar would otherwise show as well
    kept = e;
    offerInstall();
  });
  addEventListener('appinstalled', () => { kept = null; open?.close('installed'); });
}

const ios = () => html`<dialog class="install ios" aria-labelledby="install-title">
  <div class="head">
    <img src="/icon-180.png" alt="" width="48" height="48">
    <h2 id="install-title" tabindex="-1" autofocus>Install Shteker</h2>
    <form method="dialog"><button class="icon-btn" aria-label="Close">${trusted(CLOSE)}</button></form>
  </div>
  <div class="copy">
    <p>Add Shteker to your Home Screen to open it like an app. No app store.</p>
    <ol>
      <li>Tap the Share button ${trusted(SHARE)} in Safari's toolbar.</li>
      <li>Scroll down and tap Add to Home Screen ${trusted(ADD_SQUARE)}</li>
      <li>Tap Add.</li>
    </ol>
  </div>
</dialog>`;

const android = () => html`<dialog class="install android" aria-labelledby="install-title">
  <div class="copy">
    <h2 id="install-title" tabindex="-1" autofocus>Install app</h2>
    <div class="app">
      <img src="/icon-180.png" alt="" width="48" height="48">
      <div><p>Shteker</p><p class="meta">${location.host}</p></div>
    </div>
  </div>
  <form method="dialog" class="actions">
    <button class="secondary" value="cancel">Cancel</button>
    <button class="primary" type="button" data-install>Install</button>
  </form>
</dialog>`;

// Called after every screen and when the browser offers installing: it opens the popup
// only when every condition holds, so it is cheap to call.
export function offerInstall() {
  const target = installTarget(navigator);
  if (!target || !state.user || open || dismissed || dismissedRecently(store())) return;
  if (matchMedia('(display-mode: standalone)').matches || navigator.standalone) return;
  if (target === 'android' && !kept) return;
  const dialog = el(target === 'ios' ? ios() : android());
  open = dialog;
  dialog.addEventListener('close', () => {
    open = null;
    dialog.remove();
    if (dialog.returnValue !== 'installed') { dismissed = true; dismiss(store()); }
  });
  dialog.addEventListener('click', e => { if (e.target === dialog) dialog.close(); });
  dialog.querySelector('[data-install]')?.addEventListener('click', async () => {
    const event = kept;
    if (!event) return; // a second tap while the browser's prompt is open
    kept = null; // the event can be used once
    try {
      await event.prompt();
      dialog.close((await event.userChoice).outcome === 'accepted' ? 'installed' : '');
    } catch (e) {
      console.error(e);
      dialog.close();
    }
  });
  document.body.append(dialog);
  dialog.showModal();
}
